import React from "react";
import { Tag, Typography } from "antd";
import { GetDataOrderListItems } from "services/interface";
import { orderUsesPlatformShippingLabel } from "./OrderPrepareShip/prepareShipSelection";

/** ข้อความรวมสำหรับสถานะรอกรอก/ออกเลขพัสดุส่งเอง (ใช้ทั้งแท็บเตรียมจัดส่งและระหว่างจัดส่ง) */
export const LABEL_WAITING_SELF_TRACKING = "⚠️ คลิกเพื่อใส่เลขพัสดุ";

/** ความกว้างคอลัมน์「สถานะการออก Label」ให้พอข้อความ Tag บรรทัดเดียว */
export const SHIPPING_LABEL_STATUS_COLUMN_WIDTH = 200;

const tagClickableStyle: React.CSSProperties = {
  cursor: "pointer",
  lineHeight: "normal",
  padding: "4px 8px",
  textAlign: "center",
};

const AnimatedDots: React.FC = () => (
  <>
    <style>{`
      @keyframes dotBlink {
        0%, 80%, 100% { opacity: 0; }
        40% { opacity: 1; }
      }
      .shipping-label-animated-dot {
        display: inline-block;
        animation: dotBlink 1.4s infinite ease-in-out both;
      }
      .shipping-label-animated-dot:nth-child(1) { animation-delay: 0s; }
      .shipping-label-animated-dot:nth-child(2) { animation-delay: 0.2s; }
      .shipping-label-animated-dot:nth-child(3) { animation-delay: 0.4s; }
    `}</style>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
    <span className="shipping-label-animated-dot">.</span>
  </>
);

export interface ShippingLabelStatusCellProps {
  record: GetDataOrderListItems;
  queueStatus: string | undefined;
  onOpenShippingModal: (orderId: string) => void;
  onOpenOrderDetail?: (
    orderId: string,
    paymentStatus: string,
    paymentAmount: number,
    restPrice: number
  ) => void;
  /** shipped: แสดงเฉพาะออเดอร์ที่มีชิ้นส่งเอง (hasSelfShip) */
  hasSelfShip?: boolean;
  mode?: "prepare_ship" | "shipped";
  /**
   * prepare_ship ก่อนเรียกขนส่ง: มีชิ้น "ส่งเอง" ที่ยังกรอกเลขพัสดุได้ (pending)
   * และทั้งออเดอร์เป็นส่งเองล้วนหรือไม่ (selfOnly — ไม่มีชิ้นที่ต้องเรียกขนส่งแบ่งเบา)
   */
  selfTracking?: { pending: boolean; selfOnly: boolean };
  /** เปิด modal กรอกเลขพัสดุส่งเองแบบไม่ต้องเรียกขนส่ง */
  onOpenSelfTracking?: (orderId: string) => void;
}

/** ปุ่มส้ม "⚠️ คลิกเพื่อใส่เลขพัสดุ" — ใช้ในตารางและใน ModalOrderDetail */
export const SelfTrackingButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <>
    <style>{`
      .shipping-label-self-tracking-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        padding: 3px 6px;
        line-height: 1.2;
        font-size: 12px;
        font-family: inherit;
        border-radius: 6px;
        border: 1px solid #d46b08;
        background-color: #fff7e6;
        color: #d46b08;
        cursor: pointer;
        transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
      }
      .shipping-label-self-tracking-btn:hover,
      .shipping-label-self-tracking-btn:focus {
        color: #873800;
        background-color: #ffe7ba;
        border-color: #873800;
      }
    `}</style>
    <button type="button" className="shipping-label-self-tracking-btn" onClick={onClick}>
      {LABEL_WAITING_SELF_TRACKING}
    </button>
  </>
);

export const ShippingLabelStatusCell: React.FC<ShippingLabelStatusCellProps> = ({
  record,
  queueStatus: qStatus,
  onOpenShippingModal,
  onOpenOrderDetail,
  hasSelfShip = true,
  mode = "prepare_ship",
  selfTracking,
  onOpenSelfTracking,
}) => {
  if (!orderUsesPlatformShippingLabel(record)) {
    if (mode === "prepare_ship") {
      return <Tag color="orange">ไม่ต้องออก Label</Tag>;
    }
    return <Typography.Text type="secondary">-</Typography.Text>;
  }

  if (mode === "shipped" && !hasSelfShip) {
    return <Typography.Text type="secondary">-</Typography.Text>;
  }

  const notQueued = !qStatus || qStatus === "none";

  // ระหว่างจัดส่ง: ออเดอร์ที่กรอกเลขส่งเองตรงจากแท็บเตรียมจัดส่ง ไม่มี verify record → queue = none
  if (mode === "shipped" && qStatus === "none") {
    return <Typography.Text type="secondary">-</Typography.Text>;
  }

  // เตรียมจัดส่ง ก่อนเรียกขนส่ง: ชิ้น "ส่งเอง" ที่ยังไม่มีเลขพัสดุ กรอก Tracking ได้เลย
  if (mode === "prepare_ship" && notQueued && selfTracking?.pending && onOpenSelfTracking) {
    const button = <SelfTrackingButton onClick={() => onOpenSelfTracking(record.order_id)} />;
    if (selfTracking.selfOnly) return button;
    // ออเดอร์ผสม — ชิ้นแบ่งเบายังต้องเรียกขนส่ง
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
        <Tag color="yellow" style={{ marginInlineEnd: 0 }}>ยังไม้ได้เรียกขนส่ง </Tag>
        {button}
      </div>
    );
  }

  if (!qStatus) {
    return <Tag color="yellow">ยังไม้ได้เรียกขนส่ง </Tag>;
  }

  const restPrice = -(
    (record.total_order_price + record.transport_price) -
    (record.discount_amount + record.payment_amount)
  );

  switch (qStatus) {
    case "PENDING":
      return (
        <Tag
          color="purple"
          style={tagClickableStyle}
          onClick={() => onOpenShippingModal(record.order_id)}
        >
          ⏳ กำลังเชื่อมต่อกับระบบขนส่ง
          <br />
          เพื่อขอหมายเลขพัสดุ
          <AnimatedDots />
        </Tag>
      );
    case "PROCESSING":
      return (
        <Tag
          color="purple"
          style={tagClickableStyle}
          onClick={() => onOpenShippingModal(record.order_id)}
        >
          ⏳ กำลังเชื่อมต่อกับระบบขนส่ง
          <br />
          เพื่อขอหมายเลขพัสดุ
          <AnimatedDots />
        </Tag>
      );
    case "SUCCESS":
      if (mode === "shipped") {
        return <Typography.Text type="secondary">-</Typography.Text>;
      }
      return (
        <Tag
          color="green"
          style={{ cursor: "pointer" }}
          onClick={() =>
            onOpenOrderDetail?.(
              record.order_id,
              record.payment_status,
              record.payment_amount,
              restPrice
            )
          }
        >
          ✅ โปรดยืนยันการจัดส่ง
        </Tag>
      );
    case "SUCCESS_WAITING_TRACKING":
      return <SelfTrackingButton onClick={() => onOpenShippingModal(record.order_id)} />;
    case "SUCCESS_WAITING_PRINT":
      if (mode === "shipped") {
        return <Typography.Text type="secondary">-</Typography.Text>;
      }
      return (
        <Tag
          color="blue"
          style={{
            cursor: "pointer",
            whiteSpace: "normal",
            height: "auto",
            padding: "2px 6px",
            lineHeight: 1.4,
          }}
          onClick={() =>
            onOpenOrderDetail?.(
              record.order_id,
              record.payment_status,
              record.payment_amount,
              restPrice
            )
          }
        >
          ขนส่งกำลังเข้ารับสินค้า โปรด Print label
        </Tag>
      );
    case "FAILED":
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
          <Tag
            color="red"
            style={{ cursor: "pointer" }}
            onClick={() => onOpenShippingModal(record.order_id)}
          >
            ❌ ขนส่งปฏิเสธ
          </Tag>
          <Typography.Link
            style={{ fontSize: "10px" }}
            onClick={() => onOpenShippingModal(record.order_id)}
          >
            คลิกเพื่อเปลี่ยน
          </Typography.Link>
        </div>
      );
    default:
      return <Tag color="yellow">ยังไม้ได้เรียกขนส่ง </Tag>;
  }
};
